import { Container, Graphics, Text, TextStyle, Sprite, Assets, Texture } from 'pixi.js';
import { DeckCard, JokerDefinition } from '@solante/shared';

interface SpriteOptions {
  selected?: boolean;
  isActiveHand?: boolean;
  hovering?: boolean;
  /** Viewport width for responsive positioning */
  viewportWidth?: number;
  /** Viewport height for PSG1 optimization */
  viewportHeight?: number;
}

// Preload suit textures
const suitTextures: Record<string, Texture | null> = {};
const suitColors: Record<string, { primary: number; secondary: number; glow: number }> = {
  Stake: { primary: 0x9333ea, secondary: 0xa855f7, glow: 0xc084fc },
  Sol: { primary: 0xf59e0b, secondary: 0xfcd34d, glow: 0xfef3c7 },
  Burn: { primary: 0xec4899, secondary: 0xf472b6, glow: 0xfce7f3 },
  Mint: { primary: 0x06b6d4, secondary: 0x22d3ee, glow: 0xa5f3fc },
};

// Track loading state
let texturesLoading = false;
let texturesLoaded = false;
const loadCallbacks: (() => void)[] = [];

// Rank display config for visual hierarchy
const rankConfig: Record<string, { displayRank: string; isFaceCard: boolean; value: number }> = {
  A: { displayRank: 'A', isFaceCard: true, value: 14 },
  K: { displayRank: 'K', isFaceCard: true, value: 13 },
  Q: { displayRank: 'Q', isFaceCard: true, value: 12 },
  J: { displayRank: 'J', isFaceCard: true, value: 11 },
  '10': { displayRank: '10', isFaceCard: false, value: 10 },
  '9': { displayRank: '9', isFaceCard: false, value: 9 },
  '8': { displayRank: '8', isFaceCard: false, value: 8 },
  '7': { displayRank: '7', isFaceCard: false, value: 7 },
  '6': { displayRank: '6', isFaceCard: false, value: 6 },
  '5': { displayRank: '5', isFaceCard: false, value: 5 },
  '4': { displayRank: '4', isFaceCard: false, value: 4 },
  '3': { displayRank: '3', isFaceCard: false, value: 3 },
  '2': { displayRank: '2', isFaceCard: false, value: 2 },
};

// Load suit textures once
export const loadSuitTextures = async (): Promise<void> => {
  if (texturesLoaded) return;
  if (texturesLoading) {
    // Wait for existing load to complete
    return new Promise((resolve) => {
      loadCallbacks.push(resolve);
    });
  }

  texturesLoading = true;
  const suits = ['sol', 'mint', 'burn', 'stake'];

  await Promise.all(
    suits.map(async (suit) => {
      try {
        const texture = await Assets.load(`/suits/${suit}.svg`);
        if (texture && texture.valid) {
          suitTextures[suit] = texture;
        } else {
          suitTextures[suit] = null;
        }
      } catch (e) {
        console.warn(`Failed to load ${suit} texture:`, e);
        suitTextures[suit] = null;
      }
    }),
  );

  texturesLoaded = true;
  texturesLoading = false;

  // Notify waiting callbacks
  loadCallbacks.forEach((cb) => cb());
  loadCallbacks.length = 0;
};

// Check if textures are ready
export const areTexturesLoaded = (): boolean => texturesLoaded;

export const createCardSprite = (
  card: DeckCard,
  index: number,
  total: number,
  options: SpriteOptions = {},
) => {
  const cardContainer = new Container();
  const cardWidth = 130;
  const cardHeight = 185;

  // Responsive adjustments for different viewports
  const viewportWidth = options.viewportWidth ?? 720;
  const viewportHeight = options.viewportHeight ?? 480;
  const isNarrowViewport = viewportWidth < 500;
  // PSG1 detection: ~1080x1240 portrait resolution (nearly square, tall)
  const isPSG1Viewport = viewportWidth >= 1060 && viewportWidth <= 1100 && viewportHeight >= 1200;

  // Tighter spacing on narrow viewports to fit all cards
  const baseSpacing = options.isActiveHand ? 78 : 48;
  const spacing = isNarrowViewport ? baseSpacing * 0.65 : baseSpacing;
  const startX = ((total - 1) * spacing) / -2 + index * spacing;

  const suitKey = card.suit.toLowerCase();
  const colors = suitColors[card.suit] ?? suitColors.Stake;
  const config = rankConfig[card.rank] ?? { displayRank: card.rank, isFaceCard: false, value: 0 };

  // Card value glow intensity (higher cards glow more)
  const valueGlow = Math.min(config.value / 14, 1);

  // Selection glow effect
  if (options.selected) {
    const outerGlow = new Graphics();
    outerGlow.beginFill(colors.glow, 0.15);
    outerGlow.drawRoundedRect(
      -cardWidth / 2 - 12,
      -cardHeight / 2 - 12,
      cardWidth + 24,
      cardHeight + 24,
      28,
    );
    outerGlow.endFill();
    cardContainer.addChild(outerGlow);

    const innerGlow = new Graphics();
    innerGlow.beginFill(colors.primary, 0.25);
    innerGlow.drawRoundedRect(
      -cardWidth / 2 - 6,
      -cardHeight / 2 - 6,
      cardWidth + 12,
      cardHeight + 12,
      24,
    );
    innerGlow.endFill();
    cardContainer.addChild(innerGlow);
  }

  // Card background with gradient simulation
  const cardBg = new Graphics();
  // Outer dark border
  cardBg.beginFill(0x0a0f1e, 0.98);
  cardBg.drawRoundedRect(
    -cardWidth / 2 - 2,
    -cardHeight / 2 - 2,
    cardWidth + 4,
    cardHeight + 4,
    20,
  );
  cardBg.endFill();
  // Main card body with suit-tinted gradient
  const bgAlpha = options.isActiveHand ? 0.95 : 0.88;
  cardBg.beginFill(0x12192e, bgAlpha);
  cardBg.drawRoundedRect(-cardWidth / 2, -cardHeight / 2, cardWidth, cardHeight, 18);
  cardBg.endFill();
  // Suit color tint at top
  cardBg.beginFill(colors.primary, 0.15 + valueGlow * 0.1);
  cardBg.drawRoundedRect(-cardWidth / 2, -cardHeight / 2, cardWidth, cardHeight / 2, 18);
  cardBg.endFill();
  cardContainer.addChild(cardBg);

  // Card border with suit color
  const border = new Graphics();
  const borderWidth = options.selected ? 3.5 : 2.5;
  const borderAlpha = options.selected ? 0.95 : 0.6;
  border.lineStyle(borderWidth, colors.primary, borderAlpha);
  border.drawRoundedRect(-cardWidth / 2, -cardHeight / 2, cardWidth, cardHeight, 18);
  // Inner accent line
  border.lineStyle(1, colors.glow, 0.3);
  border.drawRoundedRect(
    -cardWidth / 2 + 4,
    -cardHeight / 2 + 4,
    cardWidth - 8,
    cardHeight - 8,
    14,
  );
  cardContainer.addChild(border);

  // Suit icon in center - always use fallback text for reliability
  // SVG textures can be flaky, so we'll use Unicode symbols which always work
  const suitSymbol = getSuitSymbol(card.suit);
  const suitText = new Text(
    suitSymbol,
    new TextStyle({
      fontFamily: 'system-ui, sans-serif',
      fontSize: 48,
      fill: colors.primary,
    }),
  );
  suitText.anchor.set(0.5);
  suitText.position.set(0, 10);
  cardContainer.addChild(suitText);

  // Rank display - top left corner
  const rankStyle = new TextStyle({
    fontFamily: config.isFaceCard ? '"Press Start 2P", monospace' : '"Space Grotesk", system-ui',
    fontSize: config.isFaceCard ? 24 : 28,
    fill: 0xffffff,
    fontWeight: config.isFaceCard ? 'normal' : 'bold',
    dropShadow: true,
    dropShadowColor: colors.primary,
    dropShadowBlur: 4,
    dropShadowDistance: 0,
  });
  const rankText = new Text(config.displayRank, rankStyle);
  rankText.anchor.set(0, 0);
  rankText.position.set(-cardWidth / 2 + 12, -cardHeight / 2 + 10);
  cardContainer.addChild(rankText);

  // Small suit indicator next to rank
  const miniSuitSymbol = getSuitSymbol(card.suit);
  const miniSuitText = new Text(
    miniSuitSymbol,
    new TextStyle({
      fontFamily: 'system-ui, sans-serif',
      fontSize: 16,
      fill: colors.secondary,
    }),
  );
  miniSuitText.anchor.set(0, 0);
  miniSuitText.position.set(-cardWidth / 2 + 12, -cardHeight / 2 + 42);
  cardContainer.addChild(miniSuitText);

  // Face card special decoration
  if (config.isFaceCard) {
    const crown = new Graphics();
    crown.beginFill(colors.glow, 0.15);
    // Decorative corners
    crown.drawRect(-cardWidth / 2 + 6, -cardHeight / 2 + 6, 3, 20);
    crown.drawRect(-cardWidth / 2 + 6, -cardHeight / 2 + 6, 20, 3);
    crown.drawRect(cardWidth / 2 - 9, -cardHeight / 2 + 6, 3, 20);
    crown.drawRect(cardWidth / 2 - 26, -cardHeight / 2 + 6, 20, 3);
    crown.drawRect(-cardWidth / 2 + 6, cardHeight / 2 - 26, 3, 20);
    crown.drawRect(-cardWidth / 2 + 6, cardHeight / 2 - 9, 20, 3);
    crown.drawRect(cardWidth / 2 - 9, cardHeight / 2 - 26, 3, 20);
    crown.drawRect(cardWidth / 2 - 26, cardHeight / 2 - 9, 20, 3);
    crown.endFill();
    cardContainer.addChild(crown);
  }

  // Position card in hand - responsive to viewport width
  const defaultCenterX = options.isActiveHand ? 360 : 320;
  let centerX = isNarrowViewport ? viewportWidth / 2 : defaultCenterX;
  // Position cards higher in narrow viewports for shorter canvas
  let centerY = isNarrowViewport ? 100 : options.isActiveHand ? 250 : 250;

  // PSG1 optimization: center cards with more vertical space
  if (isPSG1Viewport) {
    centerX = viewportWidth / 2;
    centerY = options.isActiveHand ? 350 : 300;
  }

  cardContainer.position.set(centerX + startX, centerY);

  // Tighter fan angle on narrow viewports
  const baseAngle = options.isActiveHand ? 6 : 4.5;
  const angle = isNarrowViewport ? baseAngle * 0.5 : baseAngle;
  cardContainer.angle = (index - (total - 1) / 2) * angle;

  cardContainer.alpha = options.isActiveHand ? 0.98 : 0.9;

  // Smaller cards on narrow viewports for compact layout
  const baseScale = options.isActiveHand ? 0.92 : 0.82;
  let scale = isNarrowViewport ? baseScale * 0.65 : baseScale;
  // PSG1 has plenty of space - use full scale
  if (isPSG1Viewport) {
    scale = options.isActiveHand ? 1.0 : 0.9;
  }
  cardContainer.scale.set(scale);

  // Selection lift effect
  if (options.selected) {
    cardContainer.position.y -= isNarrowViewport ? 15 : isPSG1Viewport ? 20 : 15;
    let selectedScale = options.isActiveHand ? 0.98 : 0.88;
    if (isNarrowViewport) {
      selectedScale = selectedScale * 0.8;
    } else if (isPSG1Viewport) {
      selectedScale = options.isActiveHand ? 1.05 : 0.95;
    }
    cardContainer.scale.set(selectedScale);
  }

  return cardContainer;
};

function getSuitSymbol(suit: string): string {
  switch (suit) {
    case 'Stake':
      return '★';
    case 'Sol':
      return '☀';
    case 'Burn':
      return '✦';
    case 'Mint':
      return '≋';
    default:
      return '◆';
  }
}

// Rarity colors for joker cards
const rarityColors: Record<string, { primary: number; secondary: number; glow: number }> = {
  common: { primary: 0x9eb6ff, secondary: 0xc4d4ff, glow: 0xe0e8ff },
  uncommon: { primary: 0x4ade80, secondary: 0x86efac, glow: 0xbbf7d0 },
  rare: { primary: 0xa855f7, secondary: 0xc084fc, glow: 0xd8b4fe },
  legendary: { primary: 0xfbbf24, secondary: 0xfcd34d, glow: 0xfef3c7 },
};

// Get effect icon based on primary effect type
function getEffectIcon(joker: JokerDefinition): string {
  if (!joker.effects || joker.effects.length === 0) return '🃏';

  const primaryEffect = joker.effects[0];
  switch (primaryEffect.kind) {
    case 'flatScore':
    case 'percentScore':
      return '⚡';
    case 'heartsOnClear':
    case 'scorePerHeart':
      return '❤️';
    case 'opponentHeartDamageOnClear':
    case 'heartStealOnClear':
      return '🎯';
    case 'chipsOnClear':
    case 'chipsOnFail':
      return '💰';
    case 'forceSuit':
      return '🎴';
    case 'handTypeFlat':
    case 'handTypePercent':
      return '🃏';
    default:
      return '✨';
  }
}

export const createJokerSprite = (
  joker: JokerDefinition,
  index: number,
  total: number,
  viewportWidth?: number,
  viewportHeight?: number,
) => {
  const jokerContainer = new Container();
  const cardWidth = 80;
  const cardHeight = 100;

  // Responsive adjustments for narrow viewports (portrait mode)
  const vw = viewportWidth ?? 720;
  const vh = viewportHeight ?? 480;
  const isNarrowViewport = vw < 500;
  // PSG1 detection: ~1080x1240 portrait resolution
  const isPSG1Viewport = vw >= 1060 && vw <= 1100 && vh >= 1200;

  const baseSpacing = 88;
  const spacing = isNarrowViewport ? baseSpacing * 0.6 : baseSpacing;
  const startX = ((total - 1) * spacing) / -2 + index * spacing;

  const colors = rarityColors[joker.rarity] ?? rarityColors.common;
  const evolutionTier = joker.evolution?.tier ?? 'base';

  // Subtle outer glow based on rarity
  const outerGlow = new Graphics();
  outerGlow.beginFill(colors.glow, 0.12);
  outerGlow.drawRoundedRect(
    -cardWidth / 2 - 4,
    -cardHeight / 2 - 4,
    cardWidth + 8,
    cardHeight + 8,
    14,
  );
  outerGlow.endFill();
  jokerContainer.addChild(outerGlow);

  // Card background with clean gradient
  const cardBg = new Graphics();
  // Dark outer edge
  cardBg.beginFill(0x080c18, 1);
  cardBg.drawRoundedRect(
    -cardWidth / 2 - 1,
    -cardHeight / 2 - 1,
    cardWidth + 2,
    cardHeight + 2,
    11,
  );
  cardBg.endFill();
  // Main card body
  cardBg.beginFill(0x0f1528, 1);
  cardBg.drawRoundedRect(-cardWidth / 2, -cardHeight / 2, cardWidth, cardHeight, 10);
  cardBg.endFill();
  // Rarity color accent at top
  cardBg.beginFill(colors.primary, 0.18);
  cardBg.drawRoundedRect(-cardWidth / 2 + 2, -cardHeight / 2 + 2, cardWidth - 4, 28, 8);
  cardBg.endFill();
  jokerContainer.addChild(cardBg);

  // Clean border with rarity color
  const border = new Graphics();
  border.lineStyle(2, colors.primary, 0.7);
  border.drawRoundedRect(-cardWidth / 2, -cardHeight / 2, cardWidth, cardHeight, 10);
  jokerContainer.addChild(border);

  // Evolution tier badge (top left, compact)
  if (evolutionTier !== 'base') {
    const tierColor = evolutionTier === 'ascended' ? 0xfbbf24 : 0xc084fc;
    const tierText = new Text(
      evolutionTier === 'ascended' ? '★★' : '★',
      new TextStyle({
        fontFamily: 'system-ui, sans-serif',
        fontSize: 10,
        fill: tierColor,
      }),
    );
    tierText.anchor.set(0, 0);
    tierText.position.set(-cardWidth / 2 + 6, -cardHeight / 2 + 4);
    jokerContainer.addChild(tierText);
  }

  // Effect icon (centered)
  const effectIcon = getEffectIcon(joker);
  const iconText = new Text(
    effectIcon,
    new TextStyle({
      fontFamily: 'system-ui, sans-serif',
      fontSize: 28,
      fill: colors.secondary,
    }),
  );
  iconText.anchor.set(0.5);
  iconText.position.set(0, -5);
  jokerContainer.addChild(iconText);

  // Joker name (bottom, clean and readable)
  const displayName = joker.name.length > 10 ? joker.name.substring(0, 9) + '…' : joker.name;
  const nameText = new Text(
    displayName,
    new TextStyle({
      fontFamily: '"Space Grotesk", system-ui',
      fontSize: 9,
      fill: 0xffffff,
      fontWeight: 'bold',
      align: 'center',
    }),
  );
  nameText.anchor.set(0.5);
  nameText.position.set(0, cardHeight / 2 - 22);
  jokerContainer.addChild(nameText);

  // Compact rarity dot indicator
  const rarityDot = new Graphics();
  rarityDot.beginFill(colors.primary, 0.9);
  const dotCount =
    joker.rarity === 'legendary'
      ? 4
      : joker.rarity === 'rare'
        ? 3
        : joker.rarity === 'uncommon'
          ? 2
          : 1;
  const dotSpacing = 8;
  const dotStartX = ((dotCount - 1) * dotSpacing) / -2;
  for (let i = 0; i < dotCount; i++) {
    rarityDot.drawCircle(dotStartX + i * dotSpacing, 0, 2.5);
  }
  rarityDot.endFill();
  rarityDot.position.set(0, cardHeight / 2 - 8);
  jokerContainer.addChild(rarityDot);

  // Position joker in row (below cards but within visible area)
  let centerX = isNarrowViewport ? vw / 2 : 360;
  // In narrow viewports, position jokers lower to avoid overlapping with cards
  let centerY = isNarrowViewport ? Math.min(vh * 0.8, vh - 60) : 430;
  let jokerScale = isNarrowViewport ? 0.65 : 0.85;

  // PSG1 optimization: better spacing with more vertical room
  if (isPSG1Viewport) {
    centerX = vw / 2;
    centerY = 550; // More space below cards on PSG1's tall screen
    jokerScale = 0.95;
  }

  jokerContainer.position.set(centerX + startX, centerY);
  jokerContainer.scale.set(jokerScale);

  return jokerContainer;
};
